import React, { useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/toast';

function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password1, setPassword1] = useState('');
  const [password2, setPassword2] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const { completeAuthSession } = useAuth();

  const handleRegister = async (e) => {
    e.preventDefault();
    setErrors({});

    if (password1 !== password2) {
      setErrors({ password2: 'Passwords do not match.' });
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await axiosInstance.post('/api/auth/registration/', {
        first_name: firstName,
        last_name: lastName,
        email,
        password1,
        password2,
      });

      await completeAuthSession(res.data);
      toast.success('Account created successfully.');
      navigate('/recipes');
    } catch (err) {
      console.error(err);
      if (err.response?.data) {
        const fieldErrors = {};
        for (const [key, value] of Object.entries(err.response.data)) {
          fieldErrors[key] = Array.isArray(value) ? value.join(' ') : value;
        }
        setErrors(fieldErrors);
        toast.error('Please fix the highlighted registration errors.');
      } else {
        setErrors({ general: 'Something went wrong during registration' });
        toast.error('Something went wrong during registration.');
      }
    } finally {
      setLoading(false);
    }
  };

  const clearPasswordMismatch = () => {
    if (errors.password2 === 'Passwords do not match.') {
      setErrors((prev) => ({ ...prev, password2: '' }));
    }
  };

  const handlePassword1Change = (e) => {
    setPassword1(e.target.value);
    clearPasswordMismatch();
  };

  const handlePassword2Change = (e) => {
    setPassword2(e.target.value);
    clearPasswordMismatch();
  };

  const inputClass =
    'w-full px-4 py-2 mt-1 border dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-400';

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 shadow-md dark:shadow-gray-900/50 rounded px-10 py-8 max-w-md w-full">
        <h2 className="type-page-title mb-6 text-center text-blue-600 dark:text-blue-400">
          Create your CartNom account
        </h2>

        {errors.general && (
          <p className="text-red-500 mb-4 text-sm text-center">{errors.general}</p>
        )}

        <form onSubmit={handleRegister} className="space-y-4">
          <div className="flex gap-4">
            <div className="flex-1">
	              <label htmlFor="first-name" className="block text-gray-700 dark:text-gray-300">First Name</label>
	              <input
	                id="first-name"
	                type="text"
                className={inputClass}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
              />
              {errors.first_name && (
                <p className="text-red-500 text-xs mt-1">{errors.first_name}</p>
              )}
            </div>
            <div className="flex-1">
	              <label htmlFor="last-name" className="block text-gray-700 dark:text-gray-300">Last Name</label>
	              <input
	                id="last-name"
	                type="text"
                className={inputClass}
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
              />
              {errors.last_name && (
                <p className="text-red-500 text-xs mt-1">{errors.last_name}</p>
              )}
            </div>
          </div>

          <div>
	            <label htmlFor="email" className="block text-gray-700 dark:text-gray-300">Email</label>
	            <input
	              id="email"
	              type="email"
              className={inputClass}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>

	          <div>
	            <label htmlFor="password1" className="block text-gray-700 dark:text-gray-300">Password</label>
	            <input
	              id="password1"
	              type={showPasswords ? 'text' : 'password'}
	              className={inputClass}
	              value={password1}
	              onChange={handlePassword1Change}
	              required
	            />
            <p className="type-muted text-xs mt-1">
              At least 8 characters
            </p>
            {errors.password1 && (
              <p className="text-red-500 text-xs mt-1">{errors.password1}</p>
            )}
          </div>

	          <div>
	            <label htmlFor="password2" className="block text-gray-700 dark:text-gray-300">Confirm Password</label>
	            <input
	              id="password2"
	              type={showPasswords ? 'text' : 'password'}
	              className={inputClass}
	              value={password2}
	              onChange={handlePassword2Change}
	              required
	            />
	            {errors.password2 && (
	              <p className="text-red-500 text-xs mt-1">{errors.password2}</p>
	            )}
	          </div>

	          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
	            <input
	              type="checkbox"
	              checked={showPasswords}
	              onChange={(e) => setShowPasswords(e.target.checked)}
	              className="rounded border-gray-300 text-blue-600 focus:ring-blue-400"
	            />
	            Show passwords
	          </label>

	          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-500 text-white py-2 rounded hover:bg-blue-600 transition disabled:opacity-50"
          >
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <p className="type-muted mt-4 text-center">
          Already have an account?{' '}
          <Link to="/login" className="text-blue-600 hover:underline">
            Log in here
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Register;
